import React, { useState, useEffect } from 'react';
import { apiCall, getUser } from '../lib/api.js';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, PieChart, Pie } from 'recharts';
import { CATEGORIES } from '../lib/scorecard-config.js';

const BAND_COLORS_HEX = { Excellent: '#10b981', Good: '#3b82f6', Okay: '#f59e0b', 'Needs improvement': '#f97316', Failed: '#ef4444' };
const ATTR_MAP = {};
CATEGORIES.forEach(cat => cat.attributes.forEach(a => { ATTR_MAP[a.id] = a.label; }));

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agentFilter, setAgentFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [evaluatorFilter, setEvaluatorFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [agents, setAgents] = useState([]);
  const [evaluators, setEvaluators] = useState([]);
  const [companies, setCompanies] = useState([]);
  const isAdmin = getUser()?.role === 'admin';
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const handleSendSummary = async (period) => {
    if (!confirm('Send ' + period + ' summary email to all agents and QA team?')) return;
    setSending(true); setSendResult(null);
    try {
      const r = await apiCall('/summary/send', { method: 'POST', body: { period } });
      setSendResult(r);
      setTimeout(() => setSendResult(null), 5000);
    } catch (e) { alert('Failed: ' + e.message); }
    setSending(false);
  };

  const fetchStats = async (agent, period, evaluator, company, from, to) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (agent) qs.set('agent', agent);
      if (period) qs.set('period', period);
      if (evaluator && evaluator !== 'all') qs.set('evaluator', evaluator);
      if (company) qs.set('company', company);
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      const data = await apiCall('/dashboard/stats?' + qs.toString());
      setStats(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => {
    fetchStats(agentFilter, periodFilter, evaluatorFilter, companyFilter, dateFrom, dateTo);
    apiCall('/agents').then(d => setAgents(d.agents || [])).catch(() => {});
    apiCall('/companies').then(d => setCompanies((d.companies || []).filter(c => !c.disabled))).catch(() => {});
    if (isAdmin) {
      apiCall('/users').then(d => setEvaluators((d.users || []).filter(u => u.role !== 'agent'))).catch(() => {});
    }
  }, []);

  const applyFilters = (agent, period, evaluator = evaluatorFilter, company = companyFilter, from = dateFrom, to = dateTo) => {
    setAgentFilter(agent);
    setPeriodFilter(period);
    setEvaluatorFilter(evaluator);
    setCompanyFilter(company);
    setDateFrom(from);
    setDateTo(to);
    fetchStats(agent, period, evaluator, company, from, to);
  };

  if (loading && !stats) return <div className="text-center py-12 text-gray-400">Loading dashboard...</div>;
  if (!stats) return <div className="text-center py-12 text-gray-400">Failed to load</div>;

  const agentChartData = stats.agents.map(a => ({
    name: a.name, score: Math.round(a.avg_score * 100), evals: a.eval_count,
  }));

  const bandData = Object.entries(stats.rating_bands).filter(([,v]) => v > 0).map(([k, v]) => ({
    name: k, value: v, color: BAND_COLORS_HEX[k] || '#9ca3af',
  }));

  const weakData = stats.weak_spots.slice(0, 6).map(w => ({
    name: ATTR_MAP[w.id] || w.id, short: w.id,
    rate: Math.round(w.issue_rate * 100), misses: w.misses, partial: w.partial,
  }));

  const periodLabel = (dateFrom || dateTo)
    ? (dateFrom || '…') + ' to ' + (dateTo || '…')
    : (periodFilter === 'all' ? 'All time' : periodFilter === 'week' ? 'This week' : periodFilter === 'month' ? 'This month' : 'This quarter');
  const companyLabel = companyFilter ? (companies.find(c => c.slug === companyFilter)?.display_name || companyFilter) : 'All companies';

  return (
    <div>
      {/* Print-only report header: the controls below are print:hidden, so this is what
          shows up on the printed/PDF page in their place. */}
      <div className="hidden print:block mb-6">
        <h1 className="text-xl font-semibold">QA Performance Report</h1>
        <div className="text-xs text-gray-500 mt-1">Generated {new Date().toLocaleString()}</div>
        <div className="text-xs text-gray-600 mt-2 space-y-0.5">
          <div>Agent: {agentFilter || 'All agents'}</div>
          <div>Period: {periodLabel}</div>
          <div>Company: {companyLabel}</div>
          {isAdmin && <div>Evaluator: {evaluatorFilter !== 'all' ? evaluatorFilter : 'All evaluators'}</div>}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5 print:hidden">
        <h2 className="text-lg font-medium">Dashboard</h2>
        <div className="flex gap-2">
          <button onClick={() => window.print()}
            className="px-3 py-1.5 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 text-xs font-medium flex items-center gap-1.5">
            <i className="ti ti-printer text-sm" aria-hidden="true"></i> Print / Save as PDF
          </button>
          <div className="relative">
            <button onClick={() => document.getElementById('send-menu').classList.toggle('hidden')} disabled={sending}
              className="px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 text-xs font-medium flex items-center gap-1.5">
              <i className="ti ti-send text-sm" aria-hidden="true"></i> {sending ? 'Sending...' : 'Send report'}
              <i className="ti ti-chevron-down text-xs" aria-hidden="true"></i>
            </button>
            <div id="send-menu" className="hidden absolute right-0 mt-1 bg-white rounded-lg border border-gray-200 shadow-lg py-1 z-10 min-w-[180px]">
              <button onClick={() => { handleSendSummary('week'); document.getElementById('send-menu').classList.add('hidden'); }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">This week</button>
              <button onClick={() => { handleSendSummary('last_week'); document.getElementById('send-menu').classList.add('hidden'); }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Last week (Mon–Fri)</button>
              <button onClick={() => { handleSendSummary('month'); document.getElementById('send-menu').classList.add('hidden'); }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">This month</button>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Agent filter */}
          <select value={agentFilter} onChange={e => applyFilters(e.target.value, periodFilter)}
            className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs bg-white focus:ring-2 focus:ring-purple-500 outline-none">
            <option value="">All agents</option>
            {agents.map(a => <option key={a.name} value={a.name}>{a.name}</option>)}
          </select>
          {/* Period filter (mutually exclusive with the custom date range below) */}
          {['all', 'week', 'month', 'quarter'].map(p => (
            <button key={p} onClick={() => applyFilters(agentFilter, p, evaluatorFilter, companyFilter, '', '')}
              className={'px-3 py-1.5 rounded-lg text-xs font-medium border transition '
                + (!dateFrom && !dateTo && periodFilter === p ? 'bg-purple-50 text-purple-700 border-purple-200' : 'border-gray-200 text-gray-500 hover:border-gray-300')}>
              {p === 'all' ? 'All time' : p === 'week' ? 'This week' : p === 'month' ? 'This month' : 'This quarter'}
            </button>
          ))}
          {/* Custom date range: overrides the period preset above while either date is set */}
          <div className="flex items-center gap-1 px-2 py-1 rounded-lg border border-gray-200 bg-white">
            <i className="ti ti-calendar text-xs text-gray-400" aria-hidden="true"></i>
            <input type="date" value={dateFrom} max={dateTo || undefined}
              onChange={e => applyFilters(agentFilter, periodFilter, evaluatorFilter, companyFilter, e.target.value, dateTo)}
              className="text-xs outline-none w-[120px]" />
            <span className="text-gray-300">–</span>
            <input type="date" value={dateTo} min={dateFrom || undefined}
              onChange={e => applyFilters(agentFilter, periodFilter, evaluatorFilter, companyFilter, dateFrom, e.target.value)}
              className="text-xs outline-none w-[120px]" />
          </div>
          {/* Company filter: any QA role (qa_scorers already see only their own work) */}
          {companies.length > 1 && (
            <select value={companyFilter} onChange={e => applyFilters(agentFilter, periodFilter, evaluatorFilter, e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs bg-white focus:ring-2 focus:ring-purple-500 outline-none">
              <option value="">All companies</option>
              {companies.map(c => <option key={c.slug} value={c.slug}>{c.display_name || c.name}</option>)}
            </select>
          )}
          {/* Evaluator filter: admins only */}
          {isAdmin && (
            <select value={evaluatorFilter} onChange={e => applyFilters(agentFilter, periodFilter, e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs bg-white focus:ring-2 focus:ring-purple-500 outline-none">
              <option value="all">All evaluators</option>
              <option value="calibration">Calibration backfill (not counted)</option>
              {evaluators.map(u => <option key={u.email} value={u.email}>{u.email}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* Active filters indicator */}
      {(agentFilter || periodFilter !== 'all' || evaluatorFilter !== 'all' || companyFilter || dateFrom || dateTo) && (
        <div className="flex items-center gap-2 mb-4 text-xs text-gray-500 print:hidden">
          <i className="ti ti-filter text-sm" aria-hidden="true"></i>
          Filtered:
          {agentFilter && <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full">{agentFilter}</span>}
          {(dateFrom || dateTo) ? (
            <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full">{dateFrom || '…'} to {dateTo || '…'}</span>
          ) : periodFilter !== 'all' && (
            <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full">{periodFilter}</span>
          )}
          {evaluatorFilter !== 'all' && <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full">{evaluatorFilter}</span>}
          {companyFilter && <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full capitalize">{companyFilter}</span>}
          <button onClick={() => applyFilters('', 'all', 'all', '', '', '')} className="text-gray-400 hover:text-gray-600 ml-1">Clear all</button>
        </div>
      )}

      {sendResult && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-xl mb-4 text-sm flex items-center gap-2 print:hidden">
          <i className="ti ti-check text-base" aria-hidden="true"></i>
          Report sent! Team summary → {sendResult.admin_recipients} managers, Individual → {sendResult.agent_recipients} agents ({sendResult.total_evals} evals, {sendResult.team_avg}% avg)
        </div>
      )}

      {/* TOP STATS */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-400">Total evaluations</div>
          <div className="text-2xl font-medium mt-1">{stats.total_evaluations}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-400">This week</div>
          <div className="text-2xl font-medium mt-1">{stats.this_week}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-400">Average score</div>
          <div className="text-2xl font-medium mt-1">{Math.round(stats.avg_score * 100)}%</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-400">This month</div>
          <div className="text-2xl font-medium mt-1">{stats.this_month}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 print:grid-cols-1 gap-4 mb-6">
        {/* AGENT PERFORMANCE */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 print:break-inside-avoid">
          <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
            <i className="ti ti-users text-purple-500" aria-hidden="true"></i> Agent performance
          </h3>
          {agentChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(140, agentChartData.length * 44)}>
              <BarChart data={agentChartData} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={v => v + '%'} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={80} />
                <Tooltip formatter={(v) => v + '%'} />
                <Bar dataKey="score" radius={[0, 4, 4, 0]} maxBarSize={28}>
                  {agentChartData.map((e, i) => <Cell key={i} fill={e.score >= 95 ? '#10b981' : e.score >= 85 ? '#3b82f6' : e.score >= 70 ? '#f59e0b' : e.score >= 50 ? '#f97316' : '#ef4444'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="text-center text-gray-400 text-sm py-8">No data</div>}
          <div className="mt-3 space-y-1">
            {stats.agents.map(a => (
              <div key={a.name} className="flex items-center justify-between text-xs text-gray-500 px-1">
                <span>{a.name}</span>
                <span>{a.eval_count} evals &middot; {a.autofails} autofails</span>
              </div>
            ))}
          </div>
        </div>

        {/* RATING DISTRIBUTION */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 print:break-inside-avoid">
          <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
            <i className="ti ti-chart-pie text-purple-500" aria-hidden="true"></i> Rating distribution
          </h3>
          {bandData.length > 0 ? (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width="50%" height={180}>
                <PieChart><Pie data={bandData} dataKey="value" cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2}>
                  {bandData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie><Tooltip /></PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">{bandData.map(b => (
                <div key={b.name} className="flex items-center gap-2 text-xs">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: b.color }}></span>
                  <span className="text-gray-600">{b.name}</span><span className="font-medium text-gray-900">{b.value}</span>
                </div>
              ))}</div>
            </div>
          ) : <div className="text-center text-gray-400 text-sm py-8">No data</div>}
        </div>
      </div>

      {/* WEAK SPOTS */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6 print:break-inside-avoid">
        <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
          <i className="ti ti-alert-triangle text-amber-500" aria-hidden="true"></i> Weak spots
          <span className="text-[11px] text-gray-400 font-normal ml-1">Highest Partial/Misses rate</span>
        </h3>
        {weakData.length > 0 ? (
          <div className="space-y-3">{weakData.map(w => (
            <div key={w.short}>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-700 truncate max-w-xs" title={w.name}>{w.name}</span>
                <span className="text-amber-600 font-medium">{w.rate}%</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-amber-400 rounded-full" style={{ width: w.rate + '%' }}></div>
              </div>
            </div>
          ))}</div>
        ) : <div className="text-center text-gray-400 text-sm py-4">No weak spots — great work!</div>}
      </div>

      {/* WEEKLY TREND */}
      {stats.weekly_trend.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 print:break-inside-avoid">
          <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
            <i className="ti ti-trending-up text-purple-500" aria-hidden="true"></i> Weekly trend
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={stats.weekly_trend.map(w => ({ ...w, score: Math.round(w.avg_score * 100) }))} margin={{ left: -10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="week" tick={{ fontSize: 10 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={v => v + '%'} />
              <Tooltip formatter={(v, n) => n === 'score' ? v + '%' : v} />
              <Bar dataKey="score" fill="#8b5cf6" radius={[4, 4, 0, 0]} maxBarSize={40} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
